import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Zap, Brain, Sparkles, Layers } from 'lucide-react';

interface SkillPillProps {
  name: string;
  level: number;
  icon: string;
  delay: number;
  category: 'language' | 'framework' | 'database' | 'other';
}

const categoryColors: Record<string, { bg: string; border: string; text: string; gradient: string }> = {
  language: {
    bg: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(0, 0, 0, 0.05)',
    text: '#0f172a',
    gradient: 'linear-gradient(135deg, #A78BFA, #F472B6)',
  },
  framework: {
    bg: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(0, 0, 0, 0.05)',
    text: '#0f172a',
    gradient: 'linear-gradient(135deg, #60A5FA, #A78BFA)',
  },
  database: {
    bg: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(0, 0, 0, 0.05)',
    text: '#0f172a',
    gradient: 'linear-gradient(135deg, #34D399, #60A5FA)',
  },
  other: {
    bg: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(0, 0, 0, 0.05)',
    text: '#0f172a',
    gradient: 'linear-gradient(135deg, #FBBF24, #F472B6)',
  },
};

const SkillPill: React.FC<SkillPillProps> = ({ name, level, icon, delay, category }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const colors = categoryColors[category];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.9, y: 12 }}
      animate={isInView ? { opacity: 1, scale: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ scale: 1.03, y: -2 }}
      className="relative flex flex-col gap-3 p-4 rounded-2xl cursor-default transition-all duration-300"
      style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
      }}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center bg-black/5 backdrop-blur-md border border-black/5 p-2 flex-shrink-0">
          <img src={icon} alt={name} className="w-full h-full object-contain drop-shadow-sm" />
        </div>
        <span
          className="text-sm font-medium whitespace-nowrap flex-1"
          style={{ color: colors.text, fontFamily: 'Inter, sans-serif' }}
        >
          {name}
        </span>
        <span
          className="text-xs font-semibold"
          style={{ color: colors.text, opacity: 0.8, fontFamily: 'Inter, sans-serif' }}
        >
          {level}%
        </span>
      </div>
      <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(0, 0, 0, 0.05)' }}>
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={isInView ? { width: `${level}%` } : {}}
          transition={{ duration: 1.2, delay: delay + 0.1, ease: 'easeOut' }}
          style={{ background: colors.gradient }}
        />
      </div>
    </motion.div>
  );
};

const Skills: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const technicalSkills: SkillPillProps[] = [
    { name: 'Python', level: 90, icon: `${process.env.PUBLIC_URL}/assets/images/TS/python.png`, delay: 0.05, category: 'language' },
    { name: 'JavaScript', level: 95, icon: `${process.env.PUBLIC_URL}/assets/images/TS/java-script.png`, delay: 0.1, category: 'language' },
    { name: 'TypeScript', level: 85, icon: `${process.env.PUBLIC_URL}/assets/images/TS/typescript.png`, delay: 0.15, category: 'language' },
    { name: 'Java', level: 80, icon: `${process.env.PUBLIC_URL}/assets/images/TS/java.png`, delay: 0.2, category: 'language' },
    { name: 'HTML', level: 95, icon: `${process.env.PUBLIC_URL}/assets/images/TS/programming.png`, delay: 0.25, category: 'language' },
    { name: 'CSS', level: 88, icon: `${process.env.PUBLIC_URL}/assets/images/TS/coding.png`, delay: 0.3, category: 'language' },
    { name: 'Django', level: 85, icon: `${process.env.PUBLIC_URL}/assets/images/TS/django (1).png`, delay: 0.35, category: 'framework' },
    { name: 'Django REST', level: 82, icon: `${process.env.PUBLIC_URL}/assets/images/TS/django.png`, delay: 0.4, category: 'framework' },
    { name: 'React', level: 88, icon: `${process.env.PUBLIC_URL}/assets/images/TS/java-script.png`, delay: 0.45, category: 'framework' },
    { name: 'MySQL', level: 80, icon: `${process.env.PUBLIC_URL}/assets/images/TS/mysql.png`, delay: 0.5, category: 'database' },
    { name: 'MongoDB', level: 75, icon: `${process.env.PUBLIC_URL}/assets/images/TS/database-storage.png`, delay: 0.55, category: 'database' },
    { name: 'DSA', level: 85, icon: `${process.env.PUBLIC_URL}/assets/images/TS/algorithm.png`, delay: 0.6, category: 'other' },
    { name: 'AIML', level: 75, icon: `${process.env.PUBLIC_URL}/assets/images/TS/ai.png`, delay: 0.65, category: 'other' },
  ];

  const softSkills = [
    { title: 'Problem Solving', desc: 'Analytical approach to complex issues', icon: `${process.env.PUBLIC_URL}/assets/images/SS/problem-solving_10645621.png` },
    { title: 'Teamwork', desc: 'Collaborative & supportive team player', icon: `${process.env.PUBLIC_URL}/assets/images/SS/partners_5371115.png` },
    { title: 'Communication', desc: 'Clear and effective articulation', icon: `${process.env.PUBLIC_URL}/assets/images/SS/language_2761083.png` },
    { title: 'Adaptability', desc: 'Quick to embrace new technologies', icon: `${process.env.PUBLIC_URL}/assets/images/SS/Adaptibility.png` },
    { title: 'Creativity', desc: 'Innovative thinking and solutions', icon: `${process.env.PUBLIC_URL}/assets/images/SS/Creativity.png` },
    { title: 'Time Mgmt', desc: 'Efficient prioritization of tasks', icon: `${process.env.PUBLIC_URL}/assets/images/SS/productive_2753156.png` },
    { title: 'Leadership', desc: 'Guiding teams toward shared goals', icon: `${process.env.PUBLIC_URL}/assets/images/SS/partners_5371115.png` },
    { title: 'Critical Thinking', desc: 'Logical evaluation and decisions', icon: `${process.env.PUBLIC_URL}/assets/images/SS/problem-solving_10645621.png` },
  ];

  const hobbies = [
    { icon: '📚', label: 'Reading Books' },
    { icon: '💻', label: 'Coding Challenges' },
    { icon: '🌱', label: 'Open Source' },
    { icon: '🎯', label: 'Competitive Programming' },
  ];

  const legendItems = [
    { category: 'language', label: 'Language' },
    { category: 'framework', label: 'Framework' },
    { category: 'database', label: 'Database' },
    { category: 'other', label: 'Other' },
  ];

  return (
    <section id="skills" className="py-24 lg:py-32 relative overflow-hidden" style={{ background: '#ffffff' }}>
      {/* Liquid Glass Background Orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div 
          className="absolute top-1/4 left-1/4 w-[30rem] h-[30rem] rounded-full mix-blend-multiply opacity-30"
          style={{ background: '#e9d5ff', filter: 'blur(120px)' }} 
        />
        <div 
          className="absolute top-1/3 right-1/4 w-[30rem] h-[30rem] rounded-full mix-blend-multiply opacity-30"
          style={{ background: '#fbcfe8', filter: 'blur(120px)' }} 
        />
        <div 
          className="absolute bottom-1/4 left-1/3 w-[30rem] h-[30rem] rounded-full mix-blend-multiply opacity-30"
          style={{ background: '#bfdbfe', filter: 'blur(120px)' }} 
        />
        
        {/* Subtle noise texture */}
        <div className="absolute inset-0 opacity-[0.02] mix-blend-overlay" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16 lg:mb-24"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ 
              background: 'rgba(255, 255, 255, 0.7)', 
              border: '1px solid rgba(0, 0, 0, 0.05)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}
          >
            <Sparkles size={16} className="text-pink-500" />
            <span className="text-sm font-medium text-slate-800 tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>Expertise</span>
          </motion.div>

          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight text-slate-900"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            Skills &{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #A78BFA, #F472B6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Technologies
            </span>
          </h2>

          <p className="text-lg max-w-2xl mx-auto font-light" style={{ color: '#475569', fontFamily: 'Inter, sans-serif' }}>
            A comprehensive overview of my technical expertise and capabilities
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">

          {/* Technical Skills */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="rounded-[2rem] p-6 sm:p-8"
            style={{
              background: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              backdropFilter: 'blur(24px)',
              boxShadow: '0 20px 40px -10px rgba(0,0,0,0.05)',
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(167, 139, 250, 0.15)' }}>
                <Layers size={18} style={{ color: '#8B5CF6' }} />
              </div>
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif', color: '#0f172a' }}>
                Technical Skills
              </h3>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-6">
              {legendItems.map(item => {
                const c = categoryColors[item.category];
                return (
                  <span
                    key={item.category}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full"
                    style={{
                      background: c.bg,
                      border: `1px solid ${c.border}`,
                      color: c.text,
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: c.gradient }} />
                    {item.label}
                  </span>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {technicalSkills.map(skill => (
                <SkillPill key={skill.name} {...skill} />
              ))}
            </div>
          </motion.div>

          {/* Soft Skills & Hobbies */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col gap-6"
          >
            {/* Soft skills */}
            <div
              className="flex-1 rounded-[2rem] p-6 sm:p-8"
              style={{
                background: 'rgba(255, 255, 255, 0.7)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(24px)',
                boxShadow: '0 20px 40px -10px rgba(0,0,0,0.05)',
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(96, 165, 250, 0.15)' }}>
                  <Brain size={18} style={{ color: '#3B82F6' }} />
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif', color: '#0f172a' }}>
                  Soft Skills
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {softSkills.map((skill, i) => (
                  <motion.div
                    key={skill.title}
                    initial={{ opacity: 0, y: 16 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.4 + i * 0.06 }}
                    whileHover={{ scale: 1.03, y: -2 }}
                    className="flex items-center gap-4 p-4 rounded-2xl cursor-default transition-all duration-300"
                    style={{
                      background: 'rgba(255, 255, 255, 0.5)',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 0, 0, 0.1)';
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.8)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 0, 0, 0.05)';
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.5)';
                    }}
                  >
                    <div className="w-12 h-12 flex-shrink-0 bg-black/5 rounded-full p-2.5 flex items-center justify-center border border-black/5">
                      <img
                        src={skill.icon}
                        alt={skill.title}
                        className="w-full h-full object-contain drop-shadow-sm"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span
                        className="text-sm font-bold leading-tight"
                        style={{ color: '#0f172a', fontFamily: 'Inter, sans-serif' }}
                      >
                        {skill.title}
                      </span>
                      <span
                        className="text-[11px] mt-1 font-medium"
                        style={{ color: '#475569', fontFamily: 'Inter, sans-serif' }}
                      >
                        {skill.desc}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Hobbies */}
            <div
              className="rounded-[2rem] p-6 sm:p-8"
              style={{
                background: 'rgba(255, 255, 255, 0.7)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(24px)',
                boxShadow: '0 20px 40px -10px rgba(0,0,0,0.05)',
              }}
            >
              <h4
                className="text-sm font-semibold mb-5 tracking-wide"
                style={{ color: '#EC4899', fontFamily: 'Inter, sans-serif' }}
              >
                Personal Interests
              </h4>
              <div className="flex flex-wrap gap-3">
                {hobbies.map((h, i) => (
                  <motion.div
                    key={h.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.7 + i * 0.08, type: 'spring', stiffness: 200 }}
                    whileHover={{ scale: 1.06, y: -2 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium cursor-default transition-all duration-300"
                    style={{
                      background: 'rgba(0, 0, 0, 0.03)',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      color: '#475569',
                      fontFamily: 'Inter, sans-serif',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(244, 114, 182, 0.4)';
                      (e.currentTarget as HTMLElement).style.background = 'rgba(244, 114, 182, 0.1)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 0, 0, 0.05)';
                      (e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.03)';
                    }}
                  >
                    <span>{h.icon}</span>
                    <span>{h.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="mt-10 rounded-[2rem] p-10 lg:p-12 text-center relative overflow-hidden"
          style={{
            background: 'rgba(255, 255, 255, 0.7)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            backdropFilter: 'blur(24px)',
            boxShadow: '0 20px 40px -10px rgba(0,0,0,0.05)',
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at center, rgba(167, 139, 250, 0.15) 0%, transparent 70%)' }}
          />
          <div className="relative z-10">
            <div
              className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{
                background: 'rgba(0, 0, 0, 0.03)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <Zap style={{ color: '#EC4899' }} size={24} />
            </div>
            <h3
              className="text-2xl font-bold mb-3"
              style={{ fontFamily: 'Syne, sans-serif', color: '#0f172a' }}
            >
              Always Learning & Growing
            </h3>
            <p className="max-w-lg mx-auto text-base" style={{ color: '#475569', fontFamily: 'Inter, sans-serif' }}>
              Continuously exploring new technologies and frameworks to stay at the forefront of web development.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;